import { EntityMetricValue } from '../../social-media/metrics-config'

/**
 * "Post Comments 20" above an empty records list is not a contradiction, and it
 * is not a broken join — the platform's metrics widget reports what IT holds,
 * and the comments widget is what we actually fetched. The two disagree about
 * coverage often enough that it is a documented trap
 * (`docs/sprinklr-connection.md` §0).
 *
 * The empty state therefore has to distinguish "we fetched nothing yet" from
 * "there was nothing to fetch", because only the first is a gap worth chasing.
 */

/** The bits of a metric bound this needs — the job's declared metric catalog. */
interface MetricBoundLike {
  metric: string
  displayName?: string
}

const MENTIONS_COMMENTS = /comment/i

/**
 * The platform's own comment count for this entity, or null if it does not
 * report one.
 *
 * The metric KEY is deliberately not hardcoded. A job declares its own metric
 * vocabulary — `postComments` here, `SN_CMT_7` somewhere else — so this matches
 * on whichever of the key or the display name says "comment", and reads the
 * value the server catalog supplied. A declared metric with no row for this
 * entity is a gap (null), not a zero: unmeasured and measured-at-zero are
 * different facts, the same rule the metric tiles follow.
 */
export const reportedCommentCount = (
  bounds: MetricBoundLike[],
  values: Record<string, EntityMetricValue>,
): number | null => {
  for (const bound of bounds) {
    const value = values[bound.metric]
    const namesComments =
      MENTIONS_COMMENTS.test(bound.metric) ||
      MENTIONS_COMMENTS.test(bound.displayName || '') ||
      MENTIONS_COMMENTS.test(value?.displayName || '')
    if (namesComments && value) return value.value
  }
  return null
}

/**
 * What to show where an entity's records would be, given how many comments the
 * platform claims it has.
 */
export const entityRecordsEmptyMessage = (
  kind: string | undefined,
  reportedComments: number | null,
): string => {
  const noun = kind || 'entity'

  // No comment metric, or none recorded for this entity: say only what is known.
  if (reportedComments === null) {
    return `No records are filed against this ${noun} yet.`
  }

  if (reportedComments === 0) {
    return `The platform reports no comments on this ${noun}, so there is nothing to fetch.`
  }

  if (reportedComments === 1) {
    return `The platform reports 1 comment on this ${noun}, but it has not been fetched into this job yet.`
  }

  return `The platform reports ${reportedComments.toLocaleString('en-GB')} comments on this ${noun}, but none have been fetched into this job yet.`
}
